import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useClientDashboard } from "../hooks/useClientDashboard";
import { clientService } from "../services/clientService";
import {
  ClientCaseDocumentFileItem,
  ClientCaseDocumentItem,
  ClientCaseCurrentStageDocumentsResponse,
  ClientJourneyStep,
  ClientTone,
} from "../types/clientPortal.types";
import UserMenu from "../ui/components/UserMenu";

const badgeClass: Record<ClientTone, string> = {
  blue: "badge-blue",
  green: "badge-green",
  amber: "badge-amber",
  red: "badge-red",
  purple: "badge-purple",
  teal: "badge-teal",
  gray: "badge-gray",
};

const formatEnumLabel = (value: string | null | undefined): string => {
  if (!value) return "Not available";
  return value
    .toLowerCase()
    .split("_")
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(" ");
};

const formatDate = (value: string | null): string => {
  if (!value) return "Not set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toneFromStageStatus = (value: string): ClientTone => {
  const normalized = value.trim().toUpperCase();
  if (normalized === "DONE") return "green";
  if (normalized === "BLOCKED") return "red";
  if (normalized === "PENDING") return "amber";
  return "blue";
};

const initialsFromName = (name: string): string => {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "CL";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const documentStatusLabel = (status: string) => status.replaceAll("_", " ");

const documentStatusBadgeClass = (status: string) => {
  switch (status) {
    case "VERIFIED":
      return "badge-green";
    case "REJECTED":
      return "badge-red";
    case "UPLOADED":
    case "UNDER_REVIEW":
      return "badge-blue";
    case "WAIVED":
      return "badge-purple";
    case "NOT_REQUESTED":
    case "REQUESTED":
    case "EXPIRED":
    default:
      return "badge-gray";
  }
};

const latestDocumentFile = (
  document: ClientCaseDocumentItem
): ClientCaseDocumentFileItem | null => {
  if (!Array.isArray(document.files) || document.files.length === 0) {
    return null;
  }
  return document.files[document.files.length - 1] ?? null;
};

const ownershipRoles = (
  document: ClientCaseDocumentItem,
  key: "submitted_by" | "verified_by"
): string[] => {
  const source = document.source_requirement_json;
  if (!source || typeof source !== "object") return [];
  const rawOwnership = (source as Record<string, unknown>).ownership;
  if (!rawOwnership || typeof rawOwnership !== "object") return [];
  const rawValue = (rawOwnership as Record<string, unknown>)[key];
  if (!Array.isArray(rawValue)) return [];
  return rawValue
    .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
    .filter((value) => Boolean(value));
};

const clientCanUploadDocument = (document: ClientCaseDocumentItem) => {
  const roles = ownershipRoles(document, "submitted_by");
  return roles.length === 0 || roles.includes("client");
};

const isAdvisorManagedUploadStage = (status: string | null | undefined): boolean => {
  const normalized = (status ?? "").trim().toLowerCase();
  return (
    normalized === "application_preparation" ||
    normalized === "internal_review"
  );
};

const toErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error !== null && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return fallback;
};

export default function ClientPage() {
  const { user } = useAuth();
  const { clientCase, journey, loading, error, refresh } = useClientDashboard();
  const [caseDocumentsData, setCaseDocumentsData] =
    useState<ClientCaseCurrentStageDocumentsResponse | null>(null);
  const [caseDocumentsLoading, setCaseDocumentsLoading] = useState(false);
  const [caseDocumentsError, setCaseDocumentsError] = useState<string | null>(null);
  const [uploadingDocumentIds, setUploadingDocumentIds] = useState<Record<string, boolean>>({});
  const [viewDocumentId, setViewDocumentId] = useState<string | null>(null);

  const loadCaseDocuments = useCallback(async () => {
    if (!clientCase) return;
    try {
      setCaseDocumentsLoading(true);
      setCaseDocumentsError(null);
      const response = await clientService.getCaseCurrentStageDocuments(clientCase.case_id);
      setCaseDocumentsData(response);
    } catch (loadError) {
      setCaseDocumentsError(
        toErrorMessage(loadError, "Could not load current-stage documents.")
      );
    } finally {
      setCaseDocumentsLoading(false);
    }
  }, [clientCase]);

  useEffect(() => {
    void loadCaseDocuments();
  }, [loadCaseDocuments]);

  const caseDocuments = caseDocumentsData?.documents ?? [];
  const uploadDisabledForClientAtStage =
    isAdvisorManagedUploadStage(
      caseDocumentsData?.lifecycle_status ?? clientCase?.lifecycle_status
    );

  const updateDocumentInState = useCallback((updated: ClientCaseDocumentItem) => {
    setCaseDocumentsData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        documents: prev.documents.map((item) =>
          item.document_item_id === updated.document_item_id ? updated : item
        ),
      };
    });
  }, []);

  const handleUploadDocument = useCallback(
    async (document: ClientCaseDocumentItem, file: File) => {
      if (!clientCase) return;
      if (uploadDisabledForClientAtStage) {
        setCaseDocumentsError("Uploads in this stage are handled by your advisor.");
        return;
      }
      setUploadingDocumentIds((prev) => ({
        ...prev,
        [document.document_item_id]: true,
      }));
      try {
        const result = await clientService.uploadCaseDocument(
          clientCase.case_id,
          document.document_item_id,
          file,
          user?.id
        );
        updateDocumentInState(result.document);
        setCaseDocumentsError(null);
      } catch (uploadError) {
        setCaseDocumentsError(toErrorMessage(uploadError, "Could not upload document."));
      } finally {
        setUploadingDocumentIds((prev) => ({
          ...prev,
          [document.document_item_id]: false,
        }));
      }
    },
    [clientCase, updateDocumentInState, uploadDisabledForClientAtStage, user?.id]
  );

  const openDocumentViewModal = useCallback((document: ClientCaseDocumentItem) => {
    if (!latestDocumentFile(document)) return;
    setViewDocumentId(document.document_item_id);
  }, []);

  const closeDocumentViewModal = useCallback(() => {
    setViewDocumentId(null);
  }, []);

  if (!clientCase || !journey) {
    return (
      <div className="app">
        <nav className="topnav">
          <div className="logo">
            <div className="logo-mark">M</div>
            CaseFlow
          </div>
          <div className="nav-right">
            <UserMenu />
          </div>
        </nav>
        <div className="page active">
          <div className="client-layout">
            <div className="client-inner">
              {loading ? (
                <section className="card">Loading client dashboard...</section>
              ) : (
                <section className="notice notice-red">
                  <i className="ti ti-alert-triangle" />
                  <span>
                    {error ?? "Could not load client dashboard."}{" "}
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        void refresh();
                      }}
                    >
                      Retry
                    </button>
                  </span>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const clientDisplayName = clientCase.primary_client_name?.trim() || "Client";
  const clientInitials = initialsFromName(clientDisplayName);
  const lifecycleTone = toneFromStageStatus(clientCase.stage_status);
  const stageRows: ClientJourneyStep[][] = [];
  for (let index = 0; index < journey.journey.steps.length; index += 6) {
    stageRows.push(journey.journey.steps.slice(index, index + 6));
  }
  const viewModalDocument =
    viewDocumentId !== null
      ? caseDocuments.find((document) => document.document_item_id === viewDocumentId) ?? null
      : null;
  const viewModalFile = viewModalDocument ? latestDocumentFile(viewModalDocument) : null;

  return (
    <div className="app">
      <nav className="topnav">
        <div className="logo">
          <div className="logo-mark">M</div>
          CaseFlow
        </div>
        <div className="nav-right">
          <span className="badge badge-blue">
            <i className="ti ti-user" /> Client portal
          </span>
          <UserMenu initials={clientInitials} />
        </div>
      </nav>

      <div className="page active">
        <div className="client-layout">
          <div className="client-inner">
            <div className="client-two-col">
              <div className="client-two-col-left">
                <div className="client-header-card">
                  <div className="avatar av-blue" style={{ width: 44, height: 44, fontSize: 15 }}>
                    {clientInitials}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{clientDisplayName}</div>
                    <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                      Stage:{" "}
                      {clientCase.current_stage_label ??
                        formatEnumLabel(clientCase.lifecycle_status)}
                    </div>
                  </div>
                  <span className={`badge ${badgeClass[lifecycleTone]}`}>
                    <i className="ti ti-clock" /> {formatEnumLabel(clientCase.lifecycle_status)}
                  </span>
                </div>

                <div className="card">
                  <div className="card-hd">
                    <i className="ti ti-briefcase" /> Your case details
                  </div>

                  <div className="client-case-summary-grid">
                    <div className="client-case-summary-item">
                      <span>Case number</span>
                      <strong>{clientCase.case_number}</strong>
                    </div>
                    <div className="client-case-summary-item">
                      <span>Case title</span>
                      <strong>{clientCase.title}</strong>
                    </div>
                    <div className="client-case-summary-item">
                      <span>Visa type</span>
                      <strong>{clientCase.visa_type}</strong>
                    </div>
                    <div className="client-case-summary-item">
                      <span>Country</span>
                      <strong>{clientCase.country_code}</strong>
                    </div>
                    <div className="client-case-summary-item">
                      <span>Stage status</span>
                      <strong>{formatEnumLabel(clientCase.stage_status)}</strong>
                    </div>
                    <div className="client-case-summary-item">
                      <span>Opened</span>
                      <strong>{formatDate(clientCase.opened_at)}</strong>
                    </div>
                    <div className="client-case-summary-item">
                      <span>Workflow template</span>
                      <strong>
                        {clientCase.workflow_template_name
                          ? `${clientCase.workflow_template_name} v${clientCase.workflow_template_version ?? 1}`
                          : "Not assigned"}
                      </strong>
                    </div>
                    <div className="client-case-summary-item">
                      <span>Adviser</span>
                      <strong>{clientCase.assigned_advisor_name ?? "Not assigned"}</strong>
                    </div>
                    <div className="client-case-summary-item">
                      <span>Assistant</span>
                      <strong>{clientCase.assigned_assistant_name ?? "Not assigned"}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="client-two-col-right">
                <div className="card">
                  <div className="card-hd">
                    <i className="ti ti-route-alt-left" /> Stages of your visa
                  </div>
                  {stageRows.map((row, rowIndex) => (
                    <div
                      key={`journey-row-${rowIndex}`}
                      className="journey-strip-full"
                      style={{ marginBottom: rowIndex === stageRows.length - 1 ? 0 : 8 }}
                    >
                      {row.map((step) => (
                        <div key={step.id} className={`jf-step ${step.state}`}>
                          <div className="jf-num">{step.number}</div>
                          <div className="jf-lbl">{step.label}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <div className="card">
                  <div className="card-hd">
                    <i className="ti ti-files" /> Documents for current stage
                  </div>
                  {caseDocumentsLoading ? (
                    <div className="assistant-stage-row">Loading stage documents...</div>
                  ) : null}
                  {caseDocumentsError ? (
                    <div className="notice notice-red" style={{ marginBottom: 10 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{caseDocumentsError}</span>
                    </div>
                  ) : null}
                  {!caseDocumentsLoading && !caseDocumentsError && caseDocuments.length === 0 ? (
                    <div className="assistant-stage-row">
                      No documents are configured for this stage.
                    </div>
                  ) : null}
                  {!caseDocumentsLoading && !caseDocumentsError && caseDocuments.length > 0 ? (
                    <>
                      <div className="assistant-stage-row" style={{ marginTop: 0 }}>
                        Current stage:{" "}
                        {caseDocumentsData?.current_stage_label ??
                          clientCase.current_stage_label ??
                          formatEnumLabel(clientCase.lifecycle_status)}
                      </div>
                      {uploadDisabledForClientAtStage ? (
                        <div className="assistant-stage-row">
                          Uploads in this stage are handled by your advisor.
                        </div>
                      ) : null}
                      <div className="advisor-documents-table-wrap">
                        <table className="advisor-documents-table">
                          <thead>
                            <tr>
                              <th>Document</th>
                              <th>Required</th>
                              <th>Status</th>
                              <th>Due</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {caseDocuments.map((document) => {
                              const latestFile = latestDocumentFile(document);
                              const canUpload =
                                !uploadDisabledForClientAtStage && clientCanUploadDocument(document);
                              const canDownload = Boolean(latestFile);
                              const canView = Boolean(latestFile);
                              return (
                                <tr key={document.document_item_id}>
                                  <td>
                                    <div className="advisor-documents-label">{document.label}</div>
                                    {document.description ? (
                                      <div className="advisor-documents-description">
                                        {document.description}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${document.is_required ? "badge-amber" : "badge-gray"}`}
                                    >
                                      {document.is_required ? "Required" : "Optional"}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge ${documentStatusBadgeClass(document.status)}`}>
                                      {documentStatusLabel(document.status)}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="advisor-documents-muted">
                                      {formatDate(document.due_at)}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="advisor-documents-actions">
                                      {canUpload ? (
                                        <label
                                          className="btn btn-sm advisor-documents-action-btn"
                                          title={
                                            uploadingDocumentIds[document.document_item_id]
                                              ? "Uploading..."
                                              : "Upload"
                                          }
                                          aria-label={
                                            uploadingDocumentIds[document.document_item_id]
                                              ? "Uploading..."
                                              : "Upload"
                                          }
                                          style={{ position: "relative", overflow: "hidden" }}
                                        >
                                          <i
                                            className={
                                              uploadingDocumentIds[document.document_item_id]
                                                ? "ti ti-loader-2"
                                                : "ti ti-upload"
                                            }
                                          />
                                          <input
                                            type="file"
                                            style={{
                                              position: "absolute",
                                              inset: 0,
                                              opacity: 0,
                                              cursor: "pointer",
                                            }}
                                            disabled={uploadingDocumentIds[document.document_item_id]}
                                            onChange={(event) => {
                                              const selectedFile = event.target.files?.[0];
                                              if (selectedFile) {
                                                void handleUploadDocument(document, selectedFile);
                                              }
                                              event.currentTarget.value = "";
                                            }}
                                          />
                                        </label>
                                      ) : null}
                                      {canDownload && latestFile ? (
                                        <a
                                          className="btn btn-sm advisor-documents-action-btn"
                                          href={latestFile.blob_url}
                                          download={latestFile.file_name}
                                          target="_blank"
                                          rel="noreferrer"
                                          title="Download"
                                          aria-label="Download"
                                        >
                                          <i className="ti ti-download" />
                                        </a>
                                      ) : null}
                                      {canView && latestFile ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm advisor-documents-action-btn"
                                          onClick={() => {
                                            openDocumentViewModal(document);
                                          }}
                                          title="View"
                                          aria-label="View"
                                        >
                                          <i className="ti ti-eye" />
                                        </button>
                                      ) : null}
                                      {!canUpload && !canDownload && !canView ? (
                                        <span className="advisor-documents-muted">No actions</span>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewModalDocument ? (
        <div className="assistant-modal-overlay" onClick={closeDocumentViewModal}>
          <section
            className="assistant-modal card assistant-document-modal"
            role="dialog"
            aria-modal="true"
            aria-label="View uploaded document"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="assistant-modal-header">
              <div className="card-hd" style={{ marginBottom: 0 }}>
                <i className="ti ti-file-search" /> View document
              </div>
              <button className="btn btn-sm" onClick={closeDocumentViewModal}>
                <i className="ti ti-x" /> Close
              </button>
            </div>

            <div className="assistant-stage-row" style={{ marginTop: 10 }}>
              {viewModalDocument.label}
            </div>

            <div className="assistant-document-preview">
              {viewModalFile ? (
                (viewModalFile.content_type ?? "").startsWith("image/") ? (
                  <img src={viewModalFile.blob_url} alt={viewModalFile.file_name} />
                ) : (
                  <iframe
                    title={`Preview ${viewModalFile.file_name}`}
                    src={viewModalFile.blob_url}
                  />
                )
              ) : (
                <div className="assistant-stage-row">No uploaded file available for preview.</div>
              )}
            </div>

            {viewModalFile ? (
              <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                File: {viewModalFile.file_name} ({Math.round(viewModalFile.size_bytes / 1024)} KB)
              </div>
            ) : null}

            <div className="assistant-modal-actions">
              <button className="btn btn-sm" onClick={closeDocumentViewModal}>
                Close
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
